import { useMutation } from "@apollo/client";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { Payment } from "generated-graphql/graphql";
import { useState } from "react";
import { prettyPrintDateTime } from "util/dates";

const RECONCILE_PAYMENT = gql(`
  mutation ReconcilePayment($id: ID!) {
    reconcilePayment(id: $id) {
      id
      reconciliationStatus
      reconciledAt
      reconciledBy
      reconciliationHistory {
        id
        previousStatus
        newStatus
        changedBy
        changedByEmail
        changedAt
      }
    }
  }
`);

const UPDATE_RECONCILIATION_STATUS = gql(`
  mutation UpdateReconciliationStatusDialog($input: UpdateReconciliationStatusInput!) {
    updateReconciliationStatus(input: $input) {
      id
      reconciliationStatus
      reconciledAt
      reconciledBy
      reconciliationHistory {
        id
        previousStatus
        newStatus
        changedBy
        changedByEmail
        changedAt
      }
    }
  }
`);

const BULK_UPDATE_RECONCILIATION_STATUS = gql(`
  mutation BulkUpdateReconciliationStatus($input: BulkUpdateReconciliationStatusInput!) {
    bulkUpdateReconciliationStatus(input: $input) {
      id
      reconciliationStatus
      reconciledAt
      reconciledBy
      reconciliationHistory {
        id
        previousStatus
        newStatus
        changedBy
        changedByEmail
        changedAt
      }
    }
  }
`);

// Import from GraphQL generated types
import { ReconciliationStatus } from "../generated-graphql/graphql";

interface ReconcilePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  payments: Payment[];
  onReconciled?: () => void;
  isBulkOperation?: boolean;
}

export function ReconcilePaymentDialog({
  open,
  onClose,
  payments,
  onReconciled,
  isBulkOperation = false,
}: ReconcilePaymentDialogProps) {
  const alerts = useAlerts();
  const isBulk = isBulkOperation || payments.length > 1;
  const singlePayment = payments[0];

  const [selectedStatus, setSelectedStatus] = useState<ReconciliationStatus>(
    isBulk
      ? ReconciliationStatus.Reconciled
      : (singlePayment?.reconciliationStatus as ReconciliationStatus) ||
          ReconciliationStatus.Unreconciled
  );

  const [updateReconciliationStatus, { loading: updatingStatus }] = useMutation(
    UPDATE_RECONCILIATION_STATUS,
    {
      onCompleted: (data) => {
        const paymentId = data.updateReconciliationStatus.id;
        const paymentUrl = `${window.location.origin}/staff/payments/${paymentId}`;

        navigator.clipboard
          .writeText(paymentUrl)
          .then(() => {
            alerts.success(
              `Payment status updated to ${selectedStatus}! Link copied to clipboard.`
            );
          })
          .catch(() => {
            alerts.success(
              `Payment status updated to ${selectedStatus}! Please manually copy the payment link for your tracking system.`
            );
          });

        onReconciled?.();
        onClose();
      },
      onError: (error) => {
        alerts.error("Failed to update reconciliation status", error);
      },
    }
  );

  const [reconcilePayment, { loading: reconciling }] = useMutation(RECONCILE_PAYMENT, {
    onCompleted: (data) => {
      const paymentId = data.reconcilePayment.id;
      const paymentUrl = `${window.location.origin}/staff/payments/${paymentId}`;

      navigator.clipboard
        .writeText(paymentUrl)
        .then(() => {
          alerts.success(
            "Payment reconciled! Link copied to clipboard. Please paste in your tracking system."
          );
        })
        .catch(() => {
          alerts.success(
            "Payment reconciled! Please manually copy the payment link for your tracking system."
          );
        });

      onReconciled?.();
      onClose();
    },
    onError: (error) => {
      alerts.error("Failed to reconcile payment", error);
    },
  });

  const [bulkUpdateReconciliationStatus, { loading: bulkUpdatingStatus }] = useMutation(
    BULK_UPDATE_RECONCILIATION_STATUS,
    {
      onCompleted: (data) => {
        const paymentUrls = data.bulkUpdateReconciliationStatus
          .map((payment: any) => `${window.location.origin}/staff/payments/${payment.id}`)
          .join("\r\n");

        navigator.clipboard
          .writeText(paymentUrls)
          .then(() => {
            alerts.success(
              `All ${payments.length} payments updated to ${selectedStatus}! Links copied to clipboard. Please paste in your tracking system.`
            );
          })
          .catch(() => {
            alerts.success(
              `All ${payments.length} payments updated to ${selectedStatus}! Please manually copy the payment links for your tracking system.`
            );
          });

        onReconciled?.();
        onClose();
      },
      onError: (error) => {
        alerts.error("Failed to update reconciliation status", error);
      },
    }
  );

  const getStatusText = (status: string) => {
    switch (status) {
      case ReconciliationStatus.Reconciled:
        return "Reconciled";
      case ReconciliationStatus.Problem:
        return "Problem";
      case ReconciliationStatus.Unreconciled:
      default:
        return "Unreconciled";
    }
  };

  const handleSubmit = async () => {
    if (isBulk) {
      // For bulk operations, use the flexible bulk update mutation
      await bulkUpdateReconciliationStatus({
        variables: {
          input: {
            paymentIds: payments.map((p) => p.id),
            newStatus: selectedStatus,
          },
        },
      });
    } else {
      // For single payments, use the flexible update mutation
      if (selectedStatus === ReconciliationStatus.Reconciled) {
        // Use the legacy reconcilePayment mutation for backward compatibility
        await reconcilePayment({
          variables: {
            id: singlePayment.id,
          },
        });
      } else {
        // Use the new updateReconciliationStatus mutation
        await updateReconciliationStatus({
          variables: {
            input: {
              paymentId: singlePayment.id,
              newStatus: selectedStatus,
            },
          },
        });
      }
    }
  };

  const isLoading = reconciling || updatingStatus || bulkUpdatingStatus;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">
            {isBulk
              ? `Update Reconciliation Status (${payments.length} payments)`
              : "Update Payment Reconciliation Status"}
          </Typography>
        </Box>
      }
      description={
        isBulk
          ? `You are about to update the reconciliation status for ${payments.length} payments. Please confirm the details below.`
          : "You are updating the reconciliation status for this payment. Please confirm the details below."
      }
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: isLoading,
        disabled: isLoading,
        saveText: isLoading ? "Saving..." : "Save",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Status Selection */}
        <FormControl fullWidth>
          <InputLabel>Reconciliation Status</InputLabel>
          <Select
            value={selectedStatus}
            label="Reconciliation Status"
            onChange={(e) => setSelectedStatus(e.target.value as ReconciliationStatus)}
          >
            <MenuItem value={ReconciliationStatus.Unreconciled}>Unreconciled</MenuItem>
            <MenuItem value={ReconciliationStatus.Reconciled}>Reconciled</MenuItem>
            <MenuItem value={ReconciliationStatus.Problem}>Problem</MenuItem>
          </Select>
        </FormControl>

        {/* Standard Operating Procedure Block */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 1,
            color: "text.secondary",
            p: 1.5,
            bgcolor: "grey.50",
            borderRadius: 1,
          }}
        >
          <strong>📋 Standard Operating Procedure</strong>
          <br />
          The finance team will reconcile payments. This is a two-part system of checks and
          balances. We are recording who is reconciling these payments, and they'll be displayed in
          the payment and in the report.
        </Typography>

        {/* Payment List for Bulk Operations */}
        {isBulk && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium", mb: 0.5 }}>
              Selected Payments ({payments.length})
            </Typography>
            <Box
              sx={{
                maxHeight: 250,
                overflow: "auto",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {payments.map((payment, index) => {
                  // Simple color cycling for visual distinction
                  const colors = ["#2196F3", "#9C27B0", "#4CAF50", "#FF9800", "#E91E63"];
                  const color = colors[index % colors.length];

                  return (
                    <Box
                      key={payment.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        py: 0.25,
                      }}
                    >
                      {/* Colored border similar to comments */}
                      <Box
                        sx={{
                          width: 3,
                          minHeight: 28,
                          backgroundColor: color,
                          borderRadius: "1.5px",
                          flexShrink: 0,
                          alignSelf: "stretch",
                        }}
                      />

                      {/* Payment content */}
                      <Box sx={{ flex: 1, pl: 0.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "medium",
                              fontSize: "0.875rem",
                            }}
                          >
                            ${payment.amount?.toLocaleString() || "0"}
                          </Typography>
                          {payment.payee && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              • {payment.payee}
                            </Typography>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Status:{" "}
                            {payment.reconciliationStatus === ReconciliationStatus.Unreconciled
                              ? "Unreconciled"
                              : payment.reconciliationStatus === ReconciliationStatus.Reconciled
                                ? "Reconciled"
                                : "Problem"}
                          </Typography>
                          {payment.paidAt && (
                            <Typography variant="caption" color="text.secondary">
                              • Paid: {prettyPrintDateTime(payment.paidAt)}
                            </Typography>
                          )}
                          {(payment as any).memo && (
                            <Typography variant="caption" color="text.secondary">
                              • {(payment as any).memo}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        {/* Confirmation Text */}
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          {isBulk
            ? `You are about to update ${
                payments.length
              } payments to ${getStatusText(selectedStatus)}.`
            : `You are updating the reconciliation status for this payment.`}{" "}
          Please confirm that:
        </Typography>

        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • You have proof of payment {isBulk ? "for each transaction" : "in hand"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • You will record {isBulk ? "these reconciliations" : "this reconciliation"} in your
            external tracking system
          </Typography>
          <Typography variant="body2">
            • The payment link{isBulk ? "s" : ""} will be copied to your clipboard for
            record-keeping
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}
